import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  useTheme,
  Stack,
  Button,
  Divider,
  useMediaQuery,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Facebook,
  LinkedIn,
  WhatsApp,
  YouTube,
  Email,
  LocationOn,
  Phone,
  School,
  Work,
  Groups,
  BusinessCenter,
  Gavel,
  HomeOutlined,
  InfoOutlined,
  ContactSupport,
  EventNote,
  ChevronRight,
  Send,
} from '@mui/icons-material';
import Logo from './Logo';

// Import a TikTok icon component
const TikTokIcon: React.FC = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 448 512" 
    fill="currentColor" 
    style={{ width: '1em', height: '1em' }}
  >
    <path d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z"/>
  </svg>
);

// Define interface for social media links
interface SocialLink {
  icon: React.ComponentType<any>;
  url: string;
  color: string;
  hoverBg: string;
}

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [email, setEmail] = React.useState('');

  // Handle email subscription
  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    // Send to contact@chishtyukimmigrationlaw.info
    console.log('Subscribed email:', email, 'to be sent to contact@chishtyukimmigrationlaw.info');
    setEmail('');
    // Show subscription confirmation (would typically use a snackbar/toast here)
    alert('Thank you for subscribing!');
  };

  // Define icon colors for services
  const serviceIconColors = {
    student: '#4CAF50', // Green for education
    work: '#2196F3',    // Blue for professional
    family: '#E91E63',  // Pink for family
    business: '#FF9800', // Orange for business
    appeals: '#9C27B0'  // Purple for legal
  };

  // Define icon colors for quick links
  const quickLinkColors = {
    home: '#00BCD4',     // Cyan
    services: '#FF9800', // Orange
    about: '#4CAF50',    // Green
    contact: '#E91E63',  // Pink
    booking: '#2196F3'   // Blue
  };

  const quickLinks = [
    { text: 'Home', path: '/', icon: HomeOutlined, color: quickLinkColors.home },
    { text: 'Services', path: '/services', icon: BusinessCenter, color: quickLinkColors.services },
    { text: 'About Us', path: '/about', icon: InfoOutlined, color: quickLinkColors.about },
    { text: 'Contact', path: '/contact', icon: ContactSupport, color: quickLinkColors.contact },
    { text: 'Book Consultation', path: '/consultation', icon: EventNote, color: quickLinkColors.booking },
  ];

  const services = [
    { text: 'Student Visas', path: '/services/student-visa', icon: School, color: serviceIconColors.student },
    { text: 'Work Visas', path: '/services/work-visa', icon: Work, color: serviceIconColors.work },
    { text: 'Family Visas', path: '/services/family-visa', icon: Groups, color: serviceIconColors.family },
    { text: 'Business Immigration', path: '/services/business-visa', icon: BusinessCenter, color: serviceIconColors.business },
    { text: 'Appeals & Reviews', path: '/services/appeals', icon: Gavel, color: serviceIconColors.appeals },
  ];

  const socialLinks: SocialLink[] = [
    { 
      icon: Facebook, 
      url: 'https://www.facebook.com/hassan.subhani.chishty.2024/', 
      color: '#1877F2',
      hoverBg: 'linear-gradient(135deg, #1877F2 0%, #2196F3 100%)'
    },
    { 
      icon: YouTube, 
      url: 'https://www.youtube.com/channel/UCGEyd0O_LfosEwlnnGh1zBA', 
      color: '#FF0000',
      hoverBg: 'linear-gradient(135deg, #FF0000 0%, #FF5252 100%)'
    },
    { 
      icon: LinkedIn, 
      url: 'https://www.linkedin.com/posts/hassan-chishty-4985b233a_ukimmigration-sponsorlicence-skilledworkervisa-activity-7315123868468170752-Y1yX?utm_source=share&utm_medium=member_android&rcm=ACoAAFiua40B_UeLVaC8DBBU0-Z6HTgMbI4DHmA', 
      color: '#0A66C2',
      hoverBg: 'linear-gradient(135deg, #0A66C2 0%, #2196F3 100%)'
    },
    { 
      icon: TikTokIcon, 
      url: 'https://www.tiktok.com/@ukvisaexpert24?_t=ZS-8vTdqA0GGKl&_r=1', 
      color: '#000000',
      hoverBg: 'linear-gradient(135deg, #25F4EE 0%, #FE2C55 100%)'
    },
    { 
      icon: WhatsApp, 
              url: 'https://wa.me/923206258495', 
      color: '#25D366',
      hoverBg: 'linear-gradient(135deg, #25D366 0%, #4CAF50 100%)'
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: theme.palette.primary.dark,
        color: 'white',
        pt: { xs: 6, md: 8 },
        pb: { xs: 4, md: 6 },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, md: 6 }}>
          {/* Logo & Company Info */}
          <Grid item xs={12} md={3}>
            <Box sx={{ mb: { xs: 3, md: 4 } }}>
              <Logo color="light" />
            </Box>
            <Typography sx={{ mb: 2, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              Expert UK immigration advice and visa services to help you achieve your immigration goals.
            </Typography>

            {/* Contact Info */}
            <Stack spacing={2} sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email sx={{ color: theme.palette.secondary.main, fontSize: isMobile ? '1.2rem' : '1.5rem' }} />
                <Typography 
                  sx={{ 
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    wordBreak: 'break-word',
                  }}
                >
                  contact@chishtyukimmigrationlaw.info
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <LocationOn 
                  sx={{ 
                    color: theme.palette.secondary.main, 
                    fontSize: isMobile ? '1.2rem' : '1.5rem',
                    mt: 0.3
                  }} 
                />
                <Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                  1 Turner Road Atif Centre High Court Lahore
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Phone 
                  sx={{ 
                    color: theme.palette.secondary.main, 
                    fontSize: isMobile ? '1.2rem' : '1.5rem'
                  }} 
                />
                <Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                  +92 320 6258495
                </Typography>
              </Box>
            </Stack>

            {/* Newsletter Subscription - Desktop & Tablet */}
            {!isMobile && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Subscribe to Newsletter
                </Typography>
                <form onSubmit={handleSubscribe}>
                  <TextField
                    fullWidth
                    placeholder="Your Email Address"
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    type="email"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.05)',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'rgba(255,255,255,0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255,255,255,0.5)',
                        },
                        color: 'white',
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255,255,255,0.7)',
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: 'rgba(255,255,255,0.5)',
                        opacity: 1,
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton 
                            edge="end" 
                            type="submit"
                            sx={{ color: theme.palette.secondary.main }}
                          >
                            <Send />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </form>
              </Box>
            )}

            {/* Social Media Links with larger icons on mobile */}
            <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
              {socialLinks.map((social, index) => {
                const SocialIcon = social.icon;
                return (
                  <IconButton
                    key={index}
                    component="a"
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Follow us on social media"
                    sx={{
                      color: social.color,
                      bgcolor: 'rgba(255,255,255,0.1)',
                      transition: 'all 0.3s ease',
                      p: { xs: 2, sm: 1.5 },
                      '&:hover': {
                        backgroundImage: social.hoverBg,
                        color: 'white',
                        transform: 'translateY(-3px)',
                      },
                      '& .MuiSvgIcon-root, & svg': {
                        fontSize: isMobile ? '2.2rem' : '1.5rem',
                      },
                    }}
                  >
                    <SocialIcon />
                  </IconButton>
                );
              })}
            </Stack>
          </Grid>

          {/* Newsletter Subscription - Mobile Only */}
          {isMobile && (
            <Grid item xs={12}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem' }}>
                  Subscribe to Newsletter
                </Typography>
                <form onSubmit={handleSubscribe}>
                  <TextField
                    fullWidth
                    placeholder="Your Email Address"
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    type="email"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.05)',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'rgba(255,255,255,0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255,255,255,0.5)',
                        },
                        color: 'white',
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255,255,255,0.7)',
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: 'rgba(255,255,255,0.5)',
                        opacity: 1,
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton 
                            edge="end" 
                            type="submit"
                            sx={{ color: theme.palette.secondary.main }}
                          >
                            <Send />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </form>
              </Box>
            </Grid>
          )}

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Quick Links
            </Typography>
            <Grid container spacing={2}>
              {quickLinks.map((link) => (
                <Grid item xs={12} key={link.text}>
                  <Button
                    component={RouterLink}
                    to={link.path}
                    startIcon={
                      <link.icon 
                        sx={{ 
                          color: link.color,
                          fontSize: { xs: '1.2rem', sm: '1.5rem' },
                          transition: 'all 0.3s ease',
                          animation: 'iconFloat 3s ease-in-out infinite',
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                          '&:hover': {
                            animation: 'iconPulse 1s ease-in-out infinite',
                            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
                          }
                        }} 
                      />
                    }
                    endIcon={
                      <ChevronRight 
                        sx={{ 
                          fontSize: { xs: '0.9rem', sm: '1rem' },
                          opacity: 0,
                          transition: 'all 0.3s ease',
                          color: link.color,
                        }} 
                      />
                    }
                    sx={{
                      color: 'white',
                      textAlign: 'left',
                      justifyContent: 'flex-start',
                      padding: { xs: '8px 12px', sm: '10px 16px' },
                      transition: 'all 0.3s ease',
                      width: '100%',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        transform: 'translateX(5px)',
                        '& .MuiButton-endIcon': {
                          opacity: 1,
                          transform: 'translateX(0)',
                        }
                      },
                      '& .MuiButton-endIcon': {
                        opacity: 0,
                        transform: 'translateX(-10px)',
                      },
                      '& .MuiButton-startIcon': {
                        marginRight: 2,
                      },
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                    }}
                  >
                    {link.text}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Our Services */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Our Services
            </Typography>
            <Grid container spacing={2}>
              {services.map((service) => (
                <Grid item xs={12} key={service.text}>
                  <Button
                    component={RouterLink}
                    to={service.path}
                    startIcon={
                      <service.icon 
                        sx={{ 
                          color: service.color,
                          fontSize: { xs: '1.2rem', sm: '1.5rem' },
                          transition: 'all 0.3s ease',
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                        }} 
                      />
                    }
                    endIcon={
                      <ChevronRight 
                        sx={{ 
                          fontSize: { xs: '0.9rem', sm: '1rem' },
                          opacity: 0,
                          transition: 'all 0.3s ease',
                          color: service.color,
                        }} 
                      />
                    }
                    sx={{
                      color: 'white',
                      textAlign: 'left',
                      justifyContent: 'flex-start',
                      padding: { xs: '8px 12px', sm: '10px 16px' },
                      transition: 'all 0.3s ease',
                      width: '100%',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        transform: 'translateX(5px)',
                        '& .MuiButton-endIcon': {
                          opacity: 1,
                          transform: 'translateX(0)',
                        }
                      },
                      '& .MuiButton-endIcon': {
                        opacity: 0,
                        transform: 'translateX(-10px)',
                      },
                      '& .MuiButton-startIcon': {
                        marginRight: 2,
                      },
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                    }}
                  >
                    {service.text}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Blog Posts */}
          <Grid item xs={12} md={3}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Latest Blog Posts
            </Typography>
            <Box component={RouterLink} to="/blog" sx={{ textDecoration: 'none' }}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                sx={{
                  my: 1,
                  borderColor: 'rgba(255,255,255,0.3)',
                  color: 'white',
                  '&:hover': {
                    borderColor: theme.palette.secondary.main,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                  },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                }}
              >
                View All Blog Posts
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 4 }} />

        {/* Copyright */}
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item xs={12} md="auto">
            <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" align={isMobile ? "center" : "left"} sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
              © {new Date().getFullYear()} Chishty UK Immigration Law. All rights reserved.
            </Typography>
          </Grid>
          
          <Grid item xs={12} md="auto">
            <Stack 
              direction={isMobile ? "column" : "row"} 
              spacing={isMobile ? 1 : 3} 
              alignItems="center" 
              justifyContent={isMobile ? "center" : "flex-end"}
            >
              <Link 
                component={RouterLink} 
                to="/privacy-policy" 
                color="inherit" 
                sx={{ 
                  opacity: 0.7, 
                  '&:hover': { opacity: 1 },
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  textAlign: isMobile ? "center" : "left"
                }}
              >
                Privacy Policy
              </Link>
              <Link 
                component={RouterLink} 
                to="/terms-and-conditions" 
                color="inherit" 
                sx={{ 
                  opacity: 0.7, 
                  '&:hover': { opacity: 1 },
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  textAlign: isMobile ? "center" : "left"
                }}
              >
                Terms of Service
              </Link>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Footer; 